import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";

/**
 * Drop-in Textarea replacement that pops a floating @-handle autocomplete.
 * Detects a `@word` at the caret, calls GET /api/users/search?q=word, and lets
 * the user pick a hit with mouse or Enter — replaces `@word` with `@handle`.
 *
 * Props: same as a native textarea plus `textareaRef` (optional external ref).
 */
export default function MentionAutocomplete({ textareaRef: extRef, className, ...props }) {
  const innerRef = useRef(null);
  const ref = extRef || innerRef;
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [triggerStart, setTriggerStart] = useState(-1);

  const closeMenu = useCallback(() => { setOpen(false); setItems([]); setTriggerStart(-1); }, []);

  const search = useCallback(async (q) => {
    try {
      const { data } = await api.get("/users/search", { params: { q, limit: 6 } });
      setItems(data.items || []);
      setActive(0);
      setOpen((data.items || []).length > 0);
    } catch { closeMenu(); }
  }, [closeMenu]);

  const handleChange = (e) => {
    props.onChange?.(e);
    const val = e.target.value;
    const caret = e.target.selectionStart || 0;
    // Find last `@` before caret with no whitespace in between
    let i = caret - 1;
    while (i >= 0 && val[i] !== "@" && !/\s/.test(val[i])) i -= 1;
    if (i >= 0 && val[i] === "@") {
      const token = val.slice(i + 1, caret);
      if (/^[a-zA-Z0-9_]*$/.test(token)) {
        setTriggerStart(i);
        if (token.length >= 1) search(token);
        else { setItems([]); setOpen(false); }
        return;
      }
    }
    closeMenu();
  };

  const pick = (u) => {
    if (!ref.current) return;
    const handle = u.handle || u.name.toLowerCase().replace(/[^a-z0-9_]/g, "");
    const el = ref.current;
    const val = el.value;
    const caret = el.selectionStart || 0;
    const before = val.slice(0, triggerStart);
    const after = val.slice(caret);
    const inserted = `@${handle} `;
    const next = before + inserted + after;
    const nextCaret = (before + inserted).length;
    // Update via native input event so React sees it
    const setter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, "value").set;
    setter.call(el, next);
    el.dispatchEvent(new Event("input", { bubbles: true }));
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(nextCaret, nextCaret);
    });
    closeMenu();
  };

  const handleKeyDown = (e) => {
    if (open && items.length > 0) {
      if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => (i + 1) % items.length); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => (i - 1 + items.length) % items.length); return; }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); pick(items[active]); return; }
      if (e.key === "Escape") { e.preventDefault(); closeMenu(); return; }
    }
    props.onKeyDown?.(e);
  };

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const off = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        // But not if clicking a suggestion (they live inside our wrapper)
        const menu = document.querySelector("[data-testid='mention-suggestions']");
        if (menu && menu.contains(e.target)) return;
        closeMenu();
      }
    };
    document.addEventListener("mousedown", off);
    return () => document.removeEventListener("mousedown", off);
  }, [open, closeMenu, ref]);

  return (
    <div className="relative">
      <textarea
        ref={ref}
        {...props}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        className={className}
      />
      {open && items.length > 0 && (
        <ul
          className="absolute left-0 right-0 top-full mt-1 z-30 max-h-60 overflow-y-auto border border-slate-200 bg-white shadow-xl"
          data-testid="mention-suggestions"
        >
          {items.map((u, i) => {
            const initials = (u.name || "?").split(" ").slice(0, 2).map((s) => s[0] || "").join("").toUpperCase();
            return (
              <li key={u.id}>
                <button
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); pick(u); }}
                  onMouseEnter={() => setActive(i)}
                  data-testid={`mention-suggestion-${u.handle || u.id}`}
                  className={`w-full text-left flex items-center gap-2 px-3 py-2 transition-colors ${
                    i === active ? "bg-slate-50 text-[#0D9488]" : "text-slate-800 hover:bg-slate-50"
                  }`}
                >
                  <div className="w-7 h-7 border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
                    {u.avatar_url ? (<img src={u.avatar_url} alt={u.name} className="w-full h-full object-cover" />) : (
                      <span className="font-display font-black text-[10px] text-slate-500">{initials}</span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm truncate">{u.name}</div>
                    {u.handle && <div className="text-[10px] font-mono-num text-slate-500 tracking-widest">@{u.handle}</div>}
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
